// The three.js side of a shaped ceiling: a mesh buffer of ceilingMesh.ts (the
// room's LOCAL plan x, y and z, in mm) as a mesh in the 3D preview's world.

import * as THREE from 'three';
import type { Vec2 } from '../../shared/types';
import type { MeshBuf } from '../ceilingMesh';

/** A local mesh buffer in world space: plan points through `W` (the room's
 *  local → world), normals turned by the room's `rotation` (degrees CCW), into
 *  three's frame (x, height, −y). UVs are millimetres, as the textures tile. */
export function meshFromBuf(buf: MeshBuf, W: (p: Vec2) => Vec2, rotation: number, mat: THREE.Material): THREE.Mesh | null {
  const n = buf.pos.length / 3;
  if (n < 3) return null;
  const pos = new Float32Array(n * 3), nrm = new Float32Array(n * 3);
  const r = (rotation * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  for (let i = 0; i < n; i++) {
    const w = W({ x: buf.pos[3 * i], y: buf.pos[3 * i + 1] });
    pos[3 * i] = w.x; pos[3 * i + 1] = buf.pos[3 * i + 2]; pos[3 * i + 2] = -w.y;
    const nx = buf.nrm[3 * i], ny = buf.nrm[3 * i + 1];
    nrm[3 * i] = c * nx - s * ny; nrm[3 * i + 1] = buf.nrm[3 * i + 2]; nrm[3 * i + 2] = -(s * nx + c * ny);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(buf.uv), 2));
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return new THREE.Mesh(geo, mat);
}
